import { authClient } from "@/lib/auth-client";

export interface OrganizationMemberRecord {
  id: string;
  name: string | null;
  email: string;
  role: string;
  image: string | null;
  joinedAt: string;
}

export interface OrganizationInvitationRecord {
  id: string;
  email: string;
  role: string;
  status: "pending" | "accepted" | "expired" | "cancelled";
  expiresAt: string;
}

export interface OrganizationRecord {
  id: string;
  name: string;
  slug: string | null;
  logo: string | null;
}

interface AuthClientError {
  message?: string | null;
  status?: number | null;
  code?: string | null;
}

interface AuthClientResult<T> {
  data?: T | null;
  error?: AuthClientError | null;
}

export type AcceptInvitationResult =
  | { kind: "success" }
  | { kind: "unauthorized" }
  | { kind: "error"; message: string };

interface OrganizationMembersPayload {
  members?: unknown[];
}

function getResultData<T>(result: unknown): T | null {
  const typed = result as AuthClientResult<T> | null;
  return typed?.data ?? null;
}

function getResultError(result: unknown): string | null {
  const typed = result as AuthClientResult<unknown> | null;
  return typed?.error?.message?.trim() || null;
}

function toStringOrNull(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function toStringValue(value: unknown, fallback = ""): string {
  return typeof value === "string" ? value : fallback;
}

function toOrganizationMember(record: unknown): OrganizationMemberRecord | null {
  if (!record || typeof record !== "object") {
    return null;
  }

  const row = record as Record<string, unknown>;
  const user = row.user && typeof row.user === "object"
    ? row.user as Record<string, unknown>
    : {};
  const id = toStringOrNull(row.id);
  const email = toStringOrNull(row.email) ?? toStringOrNull(user.email);
  if (!id || !email) {
    return null;
  }

  return {
    id,
    name: toStringOrNull(row.name) ?? toStringOrNull(user.name),
    email,
    role: toStringValue(row.role, "member"),
    image: toStringOrNull(row.image) ?? toStringOrNull(user.image),
    joinedAt: toStringValue(row.createdAt ?? row.joinedAt, new Date(0).toISOString()),
  };
}

function toOrganizationInvitation(record: unknown): OrganizationInvitationRecord | null {
  if (!record || typeof record !== "object") {
    return null;
  }

  const row = record as Record<string, unknown>;
  const id = toStringOrNull(row.id);
  const email = toStringOrNull(row.email);
  if (!id || !email) {
    return null;
  }

  const status = toStringValue(row.status, "pending");
  const normalizedStatus = status === "accepted" || status === "expired" || status === "cancelled"
    ? status
    : "pending";

  return {
    id,
    email,
    role: toStringValue(row.role, "member"),
    status: normalizedStatus,
    expiresAt: toStringValue(row.expiresAt, new Date(0).toISOString()),
  };
}

function toOrganization(record: unknown): OrganizationRecord | null {
  if (!record || typeof record !== "object") {
    return null;
  }

  const row = record as Record<string, unknown>;
  const id = toStringOrNull(row.id);
  const name = toStringOrNull(row.name);
  if (!id || !name) {
    return null;
  }

  return {
    id,
    name,
    slug: toStringOrNull(row.slug),
    logo: toStringOrNull(row.logo),
  };
}

async function requireSuccess<T>(promise: Promise<unknown>, emptyValue: T): Promise<T> {
  const result = await promise;
  const errorMessage = getResultError(result);
  if (errorMessage) {
    throw new Error(errorMessage);
  }

  return getResultData<T>(result) ?? emptyValue;
}

function getMembersFromPayload(payload: unknown): unknown[] {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (!payload || typeof payload !== "object") {
    return [];
  }

  const members = (payload as OrganizationMembersPayload).members;
  return Array.isArray(members) ? members : [];
}

export async function listOrganizationMembers(): Promise<OrganizationMemberRecord[]> {
  const payload = await requireSuccess<unknown>(
    (authClient.organization as unknown as { listMembers: () => Promise<unknown> }).listMembers(),
    { members: [] },
  );

  return getMembersFromPayload(payload)
    .map(toOrganizationMember)
    .filter((member): member is OrganizationMemberRecord => member !== null);
}

export async function listOrganizationInvitations(): Promise<OrganizationInvitationRecord[]> {
  const rows = await requireSuccess<unknown[]>(
    (authClient.organization as unknown as { listInvitations: () => Promise<unknown> }).listInvitations(),
    [],
  );

  return rows
    .map(toOrganizationInvitation)
    .filter((invitation): invitation is OrganizationInvitationRecord => invitation !== null);
}

export async function listOrganizations(): Promise<OrganizationRecord[]> {
  const rows = await requireSuccess<unknown[]>(
    (authClient.organization as unknown as { list: () => Promise<unknown> }).list(),
    [],
  );

  return rows
    .map(toOrganization)
    .filter((organization): organization is OrganizationRecord => organization !== null);
}

export async function setActiveOrganization(organizationId: string): Promise<void> {
  await requireSuccess(
    (authClient.organization as unknown as {
      setActive: (payload: { organizationId: string }) => Promise<unknown>;
    }).setActive({ organizationId }),
    null,
  );
}

export async function inviteOrganizationMember(input: {
  email: string;
  role: string;
}): Promise<void> {
  await requireSuccess(
    (authClient.organization as unknown as {
      inviteMember: (payload: { email: string; role: string }) => Promise<unknown>;
    }).inviteMember(input),
    null,
  );
}

export async function removeOrganizationMember(memberIdOrEmail: string): Promise<void> {
  await requireSuccess(
    (authClient.organization as unknown as {
      removeMember: (payload: { memberIdOrEmail: string }) => Promise<unknown>;
    }).removeMember({ memberIdOrEmail }),
    null,
  );
}

export async function cancelOrganizationInvitation(invitationId: string): Promise<void> {
  await requireSuccess(
    (authClient.organization as unknown as {
      cancelInvitation: (payload: { invitationId: string }) => Promise<unknown>;
    }).cancelInvitation({ invitationId }),
    null,
  );
}

function isUnauthorizedError(error: AuthClientError | null | undefined): boolean {
  if (!error) {
    return false;
  }
  if (typeof error.status === "number" && error.status === 401) {
    return true;
  }
  const code = error.code?.toUpperCase() ?? "";
  if (code === "UNAUTHORIZED" || code === "UNAUTHENTICATED") {
    return true;
  }
  const message = error.message?.toLowerCase() ?? "";
  return message.includes("unauthorized") || message.includes("not authenticated");
}

export async function acceptOrganizationInvitation(
  invitationId: string,
): Promise<AcceptInvitationResult> {
  try {
    const result = await (
      authClient.organization as unknown as {
        acceptInvitation: (
          payload: { invitationId: string },
        ) => Promise<AuthClientResult<unknown>>;
      }
    ).acceptInvitation({ invitationId });

    const error = result?.error ?? null;
    if (isUnauthorizedError(error)) {
      return { kind: "unauthorized" };
    }
    const message = error?.message?.trim();
    if (message) {
      return { kind: "error", message };
    }
    return { kind: "success" };
  } catch (caught) {
    const message = caught instanceof Error
      ? caught.message
      : "Failed to accept invitation.";
    return { kind: "error", message };
  }
}
